import { FC, useEffect, useState } from 'react';
import { fetchProductAttribute, ProductAttribute } from '../../api/productAttributes';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

interface AttributesDetailPanelProps {
  id: string;
  onBack: () => void;
}

const AttributesDetailPanel: FC<AttributesDetailPanelProps> = ({ id, onBack }) => {
  const [data, setData] = useState<ProductAttribute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchProductAttribute(id)
      .then((response) => {
        if (isMounted) {
          setData(response);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load attribute');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-slate-500 shadow-inner">
        Loading attribute...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-danger shadow-inner">
        {error ?? 'Attribute not found.'}
        <div className="mt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm text-white shadow"
            onClick={onBack}
          >
            Back to attributes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">

      <PageHeaderWithBack
        title={data.name}
        description={<p className="mt-1 max-w-2xl text-sm text-slate-500">
          {data.defaultValue?.length
            ? `Default option: ${data.defaultValue}`
            : 'No default option defined.'}
        </p>}
        onBack={onBack}
        backLabel="Back to attributes"
      />

      <div className="mt-6 grid gap-6 md:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Metadata</h3>
          <div className="mt-4 space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP ID</p>
              <p className="text-sm text-slate-700">{data.erpId}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Is Required</p>
              <p className="text-sm text-slate-700">
                {data.isRequired ? 'Yes' : 'No'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Default Option
              </p>
              <p className="text-sm text-slate-700">
                {data.defaultValue?.length ? data.defaultValue : '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-700">Options</h3>
            <span className="text-xs text-slate-400 ml-2">({data.options.length} total)</span>
          </div>
          {data.options.length === 0 ? (
            <p className="text-slate-400">No options available.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.options.map((option) => (
                <span
                  key={option}
                  className="rounded-full bg-primary/10 px-3 py-1 text-xs text-primary"
                >
                  {option}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttributesDetailPanel;
